import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Papa from 'papaparse';
import { z } from 'zod';
import { generalStore } from '../../../stores/GeneralStore';
import { postNotaryFile } from '../../../api/stoApi';

interface Props {
  isOpen: boolean;
  assetId: string;
  closeDialog: () => void;
  openSuccessAlert: () => void;
}
const CSVSchema = z.array(z.array(z.string()).max(1));

export const NotaryUploadDialog: FC<Props> = ({ isOpen, assetId, closeDialog, openSuccessAlert }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleCSVValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const clearState = (): void => {
      if (inputRef?.current) inputRef.current.value = '';
      setIsLoading(false);
    };
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return clearState();
    Papa.parse(file, {
      complete: async (results) => {
        try {
          CSVSchema.parse(results.data)
            .filter((row) => row[0].length > 0)
            .map((row, i) => {
              const dateString = row[0];
              const date: any = new Date(dateString);
              if (isNaN(date)) {
                const previewMaxLength = 20;
                const preview = dateString.slice(0, previewMaxLength) + (dateString.length > previewMaxLength ? '…' : '');
                throw Error(t('dashboard.assets.notary.dates.dialog.error.invalidDate', { rowNum: i + 1, preview }));
              }
              return date;
            });
          await postNotaryFile(assetId, file);
          openSuccessAlert();
        } catch (err: any) {
          let errMsg = err?.message;
          if (err instanceof z.ZodError) {
            errMsg = t('dashboard.assets.notary.dates.dialog.error.invalidFormat');
          }
          generalStore.setError('Error: ' + errMsg);
        } finally {
          clearState();
          closeDialog();
        }
      },
      header: false,
    });
  };

  const nextYear = new Date().getFullYear() + 1;

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>{t('dashboard.assets.notary.dates.dialog.title')}</DialogTitle>

      <DialogContent>
        <>
          <p style={{ whiteSpace: 'pre-line' }}>{t('dashboard.assets.notary.dates.dialog.description')}</p>
          <br />
          <i>
            <pre>
              {nextYear}-01-21T11:00:00.000Z
              <br />
              {nextYear}-01-22T11:00:00.000Z
              <br />
              {nextYear}-01-23T11:00:00.000Z
            </pre>
          </i>
        </>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="inherit" onClick={closeDialog}>
          {t('dashboard.assets.notary.dates.dialog.close')}
        </Button>
        <LoadingButton loading={isLoading} variant="contained" disableElevation color="primary" component="label">
          {t('dashboard.assets.notary.dates.dialog.upload')}
          <input ref={inputRef} hidden accept="csv/*" type="file" onChange={handleCSVValidation} />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
